import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { 
    Ticket, 
    Users, 
    BarChart3, 
    Settings, 
    MessageSquare, 
    Shield,
    Moon,
    Sun,
    ArrowRight,
    Check,
    Zap,
    Clock,
    Star
} from 'lucide-react';

const DISCORD_CLIENT_ID = "1477751030426112261";
const BOT_INVITE_URL = `https://discord.com/api/oauth2/authorize?client_id=${DISCORD_CLIENT_ID}&permissions=8&scope=bot%20applications.commands`;

const DiscordIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/>
    </svg>
);

const features = [
    {
        icon: Ticket,
        title: 'Ticket Management',
        description: 'Create and manage support tickets with categories, priorities, and assignments.'
    },
    {
        icon: Users,
        title: 'Staff Teams',
        description: 'Organize your support staff into teams with different permissions and roles.'
    },
    {
        icon: BarChart3,
        title: 'Analytics',
        description: 'Track ticket statistics, response times, and staff performance in real-time.'
    },
    {
        icon: MessageSquare,
        title: 'Custom Messages',
        description: 'Personalize welcome, close, and notification messages for your server.'
    },
    {
        icon: Shield,
        title: 'Transcripts',
        description: 'Automatically save ticket conversations for future reference and auditing.'
    },
    {
        icon: Settings,
        title: 'Easy Setup',
        description: 'Configure everything through our intuitive web dashboard in minutes.'
    }
];

const stats = [
    { value: '10K+', label: 'Servers' },
    { value: '1M+', label: 'Tickets' },
    { value: '99.9%', label: 'Uptime' },
    { value: '24/7', label: 'Support' }
];

export default function Landing() {
    const { login, user } = useAuth();
    const { theme, toggleTheme } = useTheme();

    return (
        <div className="min-h-screen bg-background">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 border-b bg-background/80 backdrop-blur-xl">
                <div className="container mx-auto px-4 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                            <Ticket className="w-5 h-5 text-primary-foreground" />
                        </div>
                        <span className="text-xl font-bold">TicketBot</span>
                    </div>
                    
                    <div className="flex items-center gap-4">
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-lg hover:bg-muted transition-colors"
                            data-testid="theme-toggle"
                        >
                            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </button>
                        
                        {user ? (
                            <Button onClick={() => window.location.href = '/dashboard'} data-testid="go-dashboard-btn">
                                Dashboard
                            </Button>
                        ) : (
                            <Button onClick={login} className="discord-btn flex items-center gap-2" data-testid="login-btn">
                                <DiscordIcon />
                                Login with Discord
                            </Button>
                        )}
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-3xl opacity-50" />
                
                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary mb-8">
                            <Zap className="w-4 h-4" />
                            <span className="text-sm font-medium">The #1 Discord Ticket Bot</span>
                        </div>
                        
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
                            Streamline Your
                            <span className="text-gradient block">Discord Support</span>
                        </h1>
                        
                        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
                            The most powerful and user-friendly ticket bot for Discord. 
                            Manage support tickets, track analytics, and deliver exceptional member experiences.
                        </p>
                        
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Button 
                                size="lg" 
                                onClick={() => window.open(BOT_INVITE_URL, '_blank')}
                                className="discord-btn flex items-center gap-2 text-lg px-8 py-6"
                                data-testid="hero-login-btn"
                            >
                                <DiscordIcon />
                                Add to Discord
                                <ArrowRight className="w-5 h-5" />
                            </Button>
                            
                            <Button 
                                size="lg" 
                                variant="outline"
                                className="flex items-center gap-2 text-lg px-8 py-6"
                                data-testid="hero-dashboard-btn"
                                onClick={() => user ? window.location.href = '/dashboard' : login()}
                            >
                                View Dashboard
                            </Button>
                        </div>
                    </motion.div>

                    {/* Stats */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20 max-w-3xl mx-auto"
                    >
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="text-3xl md:text-4xl font-bold text-primary font-mono">{stat.value}</div>
                                <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
                            </div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 bg-muted/30">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-3xl md:text-5xl font-bold mb-4">Why Choose TicketBot?</h2>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                            Everything you need to provide exceptional support to your Discord community.
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="feature-card group"
                            >
                                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                    <feature.icon className="w-6 h-6 text-primary" />
                                </div>
                                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                                <p className="text-muted-foreground">{feature.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20">
                <div className="container mx-auto px-4">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <h2 className="text-3xl md:text-4xl font-bold mb-6">
                                Powerful Dashboard for Complete Control
                            </h2>
                            <p className="text-lg text-muted-foreground mb-8">
                                Manage everything from one intuitive web interface. No commands to memorize, 
                                no complicated setup. Just login and start managing.
                            </p>
                            
                            <div className="space-y-4">
                                {[
                                    'Real-time ticket statistics and analytics',
                                    'Staff performance tracking',
                                    'Custom categories and priorities',
                                    'Automated ticket transcripts',
                                    'Multi-server management'
                                ].map((item, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                                            <Check className="w-4 h-4 text-green-500" />
                                        </div>
                                        <span>{item}</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="relative"
                        >
                            <div className="aspect-video rounded-xl bg-card border overflow-hidden shadow-2xl">
                                <img 
                                    src="https://images.unsplash.com/photo-1764258057604-94e8e6ca092f?w=800&h=450&fit=crop"
                                    alt="Dashboard Preview"
                                    className="w-full h-full object-cover opacity-80"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-primary/5 border-y">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center max-w-2xl mx-auto"
                    >
                        <Star className="w-12 h-12 text-primary mx-auto mb-6" />
                        <h2 className="text-3xl md:text-4xl font-bold mb-4">
                            Ready to Get Started?
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8">
                            Join thousands of Discord servers already using TicketBot. 
                            Setup takes less than 2 minutes.
                        </p>
                        <Button 
                            size="lg"
                            onClick={() => window.open(BOT_INVITE_URL, '_blank')}
                            className="discord-btn flex items-center gap-2 text-lg px-8 py-6 mx-auto"
                            data-testid="cta-login-btn"
                        >
                            <DiscordIcon />
                            Add to Discord
                            <ArrowRight className="w-5 h-5" />
                        </Button>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 border-t">
                <div className="container mx-auto px-4">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                                <Ticket className="w-4 h-4 text-primary-foreground" />
                            </div>
                            <span className="font-semibold">TicketBot</span>
                        </div>
                        
                        <div className="flex items-center gap-6 text-sm text-muted-foreground">
                            <a href="#" className="hover:text-foreground transition-colors">Privacy</a>
                            <a href="#" className="hover:text-foreground transition-colors">Terms</a>
                            <a href="#" className="hover:text-foreground transition-colors">Support</a>
                        </div>
                        
                        <p className="text-sm text-muted-foreground">
                            {new Date().getFullYear()} TicketBot. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
